import type { BoxProps } from '@mui/material/Box';
import Box from '@mui/material/Box';

import { varAlpha } from 'minimal-shared/utils';

import type { SettingsState } from '../types';
import { OptionButton } from './styles';

export type NavLayoutOptionProps = BoxProps & {
    value: SettingsState['navLayout'];
    options: {
        value: SettingsState['navLayout'];
        icon: React.ReactNode;
    }[];
    onChangeOption: (newOption: SettingsState['navLayout']) => void;
};

/**
 * A component that displays layout options as a grid of buttons.
 *
 * @param {Object} props - The component props
 * @param {SxProps} [props.sx] - The system prop that allows defining system overrides as well as additional CSS styles
 * @param {string | number} props.value - The currently selected option value
 * @param {Array<{value: string | number, icon: React.ReactNode}>} props.options - Array of option objects, each with a value and icon
 * @param {Function} props.onChangeOption - Callback fired when an option is selected, receives the option value as parameter
 *
 * @returns {JSX.Element} A grid of selectable option buttons
 */
export function NavLayoutOptions({
    sx,
    value,
    options,
    onChangeOption,
    ...other
}: NavLayoutOptionProps) {
    return (
        <Box
            sx={[
                {
                    gap: 1.5,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...other}
        >
            {options.map((option) => {
                const selected = value === option.value;

                return (
                    <OptionButton
                        key={option.value}
                        selected={selected}
                        onClick={() => onChangeOption(option.value)}
                        sx={[
                            (theme) => ({
                                height: 64,
                                border: `solid 1px ${varAlpha(theme.vars.palette.grey['500Channel'], 0.08)}`,
                            }),
                        ]}
                    >
                        {option.icon}
                    </OptionButton>
                );
            })}
        </Box>
    );
}

export type NavColorOptionProps = BoxProps & {
    value: SettingsState['navColor'];
    options: {
        label: string;
        value: SettingsState['navColor'];
        icon: React.ReactNode;
    }[];
    onChangeOption: (newOption: SettingsState['navColor']) => void;
};

/**
 * NavColorOptions component allows users to select from a grid of color options.
 * It displays color options as a 2-column grid of buttons where each option includes an icon and label.
 *
 * @param {object} props - Component props
 * @param {SxProps} props.sx - The system prop that allows defining system overrides as well as additional CSS styles
 * @param {string} props.value - Currently selected option value
 * @param {Array<{value: string, label: string, icon: ReactNode}>} props.options - Array of available options
 * @param {function} props.onChangeOption - Callback function called when an option is selected
 * @returns {JSX.Element} A grid of selectable color options
 */
export function NavColorOptions({
    sx,
    value,
    options,
    onChangeOption,
    ...other
}: NavColorOptionProps) {
    return (
        <Box
            sx={[
                {
                    gap: 1.5,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...other}
        >
            {options.map((option) => {
                const selected = value === option.value;

                return (
                    <OptionButton
                        key={option.value}
                        selected={selected}
                        onClick={() => onChangeOption(option.value)}
                        sx={{ gap: 1.5, height: 56 }}
                    >
                        {option.icon}
                        {option.label}
                    </OptionButton>
                );
            })}
        </Box>
    );
}
